// Animações de "celebração ao acertar" no Modo Estudo. Porte fiel das 6
// variantes do Simulador legado (Simulador.html ~10693-11387):
//   1) confete  — explosão de papelzinhos coloridos
//   2) fogos    — anéis radiais com brilho aditivo
//   3) estrelas — chuva de estrelas douradas com twinkle
//   4) emojis   — chuva de 🎉✨⭐🏆💯
//   5) baloes   — balões coloridos subindo com barbante
//   6) champanhe — duas taças deslizam, clink + faíscas/respingos
//
// Todas usam canvas full-screen position:fixed, z-index alto, pointer-events
// none. requestAnimationFrame até `alive=false` ou `elapsed > duration`, e
// daí `canvas.remove()` libera memória.
//
// Disparo:
//   - `fireCorrectAnimation()` sorteia uma variante habilitada (master + set)
//     e respeita prefers-reduced-motion + _lastCorrectAnim (evita repetir).
//   - `previewAnimationVariant(name)` toca direto, ignora estado, pra preview
//     no modal de preferências.
//
// Vibração (feedback tátil em mobile):
//   - `vibrate(type)` despacha pra navigator.vibrate respeitando o pref
//     master e o pref granular (correct/wrong/tap).
//
// Toda a leitura/escrita de prefs vai via getter passado em initialize().

export type AnimationVariant =
  | "confete"
  | "fogos"
  | "estrelas"
  | "emojis"
  | "baloes"
  | "champanhe";

export const ANIMATION_VARIANTS: AnimationVariant[] = [
  "confete",
  "fogos",
  "estrelas",
  "emojis",
  "baloes",
  "champanhe"
];

export type VibrationType = "correct" | "wrong" | "tap";

export interface AnimationsPrefsGetter {
  /** master toggle: se false, nenhuma animação dispara */
  enabled: () => boolean;
  /** quais variantes podem entrar no sorteio */
  variants: () => Record<AnimationVariant, boolean>;
  /** master de vibração */
  vibrationEnabled: () => boolean;
  /** sub-toggles de vibração */
  vibrationGranular: () => Record<VibrationType, boolean>;
}

let _prefs: AnimationsPrefsGetter | null = null;
let _lastVariant: AnimationVariant | null = null;

export function initAnimations(getter: AnimationsPrefsGetter): void {
  _prefs = getter;
}

function reducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  if (!window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Dispara uma animação de acerto. Sorteia entre as variantes habilitadas,
 * evitando repetir a última. Silencioso se prefs ausente, master off, ou
 * conjunto vazio.
 */
export function fireCorrectAnimation(): void {
  if (!_prefs) return;
  if (!_prefs.enabled()) return;
  if (reducedMotion()) return;
  const variantsState = _prefs.variants();
  const enabled = ANIMATION_VARIANTS.filter((v) => variantsState[v]);
  if (enabled.length === 0) return;
  let candidates = enabled.filter((v) => v !== _lastVariant);
  if (candidates.length === 0) candidates = enabled;
  const variant = candidates[Math.floor(Math.random() * candidates.length)];
  _lastVariant = variant;
  playVariant(variant);
}

/**
 * Preview de uma variante específica — usado no modal de preferências
 * quando o aluno toca pra ouvir/ver o efeito. Ignora master e
 * _lastVariant, mas respeita prefers-reduced-motion.
 */
export function previewAnimationVariant(variant: AnimationVariant): void {
  if (reducedMotion()) return;
  playVariant(variant);
}

function playVariant(variant: AnimationVariant): void {
  switch (variant) {
    case "confete":
      playConfettiBurst();
      return;
    case "fogos":
      playFireworks();
      return;
    case "estrelas":
      playStarsRain();
      return;
    case "emojis":
      playEmojiRain();
      return;
    case "baloes":
      playBalloonsRise();
      return;
    case "champanhe":
      playChampagneToast();
      return;
  }
}

// =========================================================================
// Vibração
// =========================================================================

const VIBRATION_PATTERNS: Record<VibrationType, number | number[]> = {
  correct: 60, // pulso curto
  wrong: [40, 80, 40], // shake
  tap: 15 // toque leve
};

export function vibrate(type: VibrationType): void {
  if (!_prefs) return;
  if (typeof navigator === "undefined" || !navigator.vibrate) return;
  if (!_prefs.vibrationEnabled()) return;
  if (!_prefs.vibrationGranular()[type]) return;
  try {
    navigator.vibrate(VIBRATION_PATTERNS[type]);
  } catch {
    // ignora (iOS Safari etc. lança às vezes)
  }
}

// =========================================================================
// Canvas helper
// =========================================================================

function makeAnimCanvas(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed; inset:0; pointer-events:none; z-index:99999;";
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  return canvas;
}

// =========================================================================
// 1) Confete — explosão central + duas laterais com delay + faíscas
// =========================================================================

interface ConfettiParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: string;
  rot: number;
  vrot: number;
  shape: "rect" | "circle" | "streamer" | "spark";
  wobble: number;
  vwobble: number;
}

function playConfettiBurst(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const colors = [
    "#3b82f6",
    "#22c55e",
    "#eab308",
    "#ef4444",
    "#a855f7",
    "#ec4899",
    "#7665fa",
    "#f97316",
    "#fde047",
    "#34d399"
  ];
  const particles: ConfettiParticle[] = [];
  const cx = canvas.width / 2;
  const cy = canvas.height * 0.55;

  function burst(
    originX: number,
    originY: number,
    count: number,
    speedRange: [number, number]
  ): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed =
        speedRange[0] + Math.random() * (speedRange[1] - speedRange[0]);
      const r = Math.random();
      const shape: ConfettiParticle["shape"] =
        r < 0.55 ? "rect" : r < 0.85 ? "circle" : "streamer";
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 7,
        w: 5 + Math.random() * 8,
        h:
          shape === "streamer"
            ? 16 + Math.random() * 14
            : 6 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.55,
        shape,
        wobble: Math.random() * Math.PI * 2,
        vwobble: 0.06 + Math.random() * 0.09
      });
    }
  }

  function sparks(count: number): void {
    for (let i = 0; i < count; i++) {
      particles.push({
        x: cx + (Math.random() - 0.5) * canvas.width * 0.7,
        y: canvas.height + 10,
        vx: (Math.random() - 0.5) * 3,
        vy: -(11 + Math.random() * 10),
        w: 3,
        h: 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        rot: 0,
        vrot: 0,
        shape: "spark",
        wobble: 0,
        vwobble: 0
      });
    }
  }

  const widthFactor = Math.min(1.2, Math.max(0.7, canvas.width / 900));
  burst(cx, cy, Math.round(110 * widthFactor), [6, 15]);
  sparks(Math.round(28 * widthFactor));
  setTimeout(
    () =>
      burst(
        cx - canvas.width * 0.28,
        cy + 30,
        Math.round(70 * widthFactor),
        [5, 13]
      ),
    180
  );
  setTimeout(
    () =>
      burst(
        cx + canvas.width * 0.28,
        cy + 30,
        Math.round(70 * widthFactor),
        [5, 13]
      ),
    360
  );
  setTimeout(() => sparks(Math.round(20 * widthFactor)), 280);

  const start = performance.now();
  const duration = 3200;
  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = false;
    const fade = Math.max(0, 1 - elapsed / duration);
    for (const p of particles) {
      p.vy += p.shape === "spark" ? 0.12 : 0.3;
      p.vx *= 0.992;
      if (p.shape === "spark") {
        p.x += p.vx;
      } else {
        p.wobble += p.vwobble;
        p.x += p.vx + Math.sin(p.wobble) * 0.6;
      }
      p.y += p.vy;
      p.rot += p.vrot;
      if (p.y < canvas.height + 60 && p.y > -60 && fade > 0) {
        alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = fade;
        ctx.fillStyle = p.color;
        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "streamer") {
          ctx.fillRect(-p.w / 4, -p.h / 2, p.w / 2, p.h);
        } else if (p.shape === "spark") {
          ctx.fillRect(-1.5, -1.5, 3, 3);
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
    }
    if (alive && elapsed < duration + 800) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}

// =========================================================================
// 2) Fogos — anéis radiais com globalCompositeOperation 'lighter'
// =========================================================================

interface FireworkParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  life: number;
  trail: Array<{ x: number; y: number }>;
}

interface FireworkFlash {
  x: number;
  y: number;
  life: number;
  color: string;
}

function playFireworks(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const colors = [
    "#ef4444",
    "#3b82f6",
    "#22c55e",
    "#eab308",
    "#a855f7",
    "#ec4899",
    "#7665fa",
    "#fde047",
    "#f97316"
  ];
  const particles: FireworkParticle[] = [];
  const flashes: FireworkFlash[] = [];

  function explode(x: number, y: number): void {
    const color = colors[Math.floor(Math.random() * colors.length)];
    flashes.push({ x, y, life: 1, color });
    const count = 60 + Math.floor(Math.random() * 18);
    const baseSpeed = 4.5 + Math.random() * 2;
    for (let i = 0; i < count; i++) {
      const angle =
        (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.05;
      const speed = baseSpeed * (0.92 + Math.random() * 0.16);
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        life: 1,
        trail: []
      });
    }
  }

  const positions: Array<[number, number]> = [
    [canvas.width * 0.3, canvas.height * 0.35],
    [canvas.width * 0.7, canvas.height * 0.28],
    [canvas.width * 0.5, canvas.height * 0.42],
    [canvas.width * 0.2, canvas.height * 0.4],
    [canvas.width * 0.8, canvas.height * 0.38]
  ];
  explode(positions[0][0], positions[0][1]);
  for (let i = 1; i < positions.length; i++) {
    const pos = positions[i];
    setTimeout(() => explode(pos[0], pos[1]), i * 280);
  }

  const start = performance.now();
  const duration = 3000;
  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = "lighter";
    let alive = false;

    for (const f of flashes) {
      if (f.life > 0) {
        alive = true;
        const r = (1 - f.life) * 80 + 20;
        const grad = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, r);
        grad.addColorStop(0, `rgba(255, 255, 255, ${f.life * 0.9})`);
        grad.addColorStop(0.5, f.color);
        grad.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.globalAlpha = f.life;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
        ctx.fill();
        f.life -= 0.06;
      }
    }

    for (const p of particles) {
      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > 14) p.trail.shift();
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.life -= 0.014;
      if (p.life > 0) {
        alive = true;
        ctx.fillStyle = p.color;
        for (let i = 0; i < p.trail.length; i++) {
          const tp = p.trail[i];
          ctx.globalAlpha = (i / p.trail.length) * p.life * 0.55;
          ctx.beginPath();
          ctx.arc(tp.x, tp.y, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = p.life;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    if (alive && elapsed < duration + 800) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}

// =========================================================================
// 3) Estrelas — chuva dourada com twinkle
// =========================================================================

interface StarParticle {
  x: number;
  y: number;
  vy: number;
  vx: number;
  size: number;
  color: string;
  rot: number;
  vrot: number;
  twinkle: number;
  vtwinkle: number;
}

function playStarsRain(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const colors = ["#fbbf24", "#fde047", "#facc15", "#fcd34d", "#f59e0b"];
  const particles: StarParticle[] = [];
  const widthFactor = Math.min(1.4, Math.max(0.8, canvas.width / 900));
  const total = Math.round(55 * widthFactor);

  for (let i = 0; i < total; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: -50 - Math.random() * canvas.height * 0.5,
      vy: 4.5 + Math.random() * 5,
      vx: (Math.random() - 0.5) * 1,
      size: 7 + Math.random() * 13,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.1,
      twinkle: Math.random() * Math.PI * 2,
      vtwinkle: 0.08 + Math.random() * 0.08
    });
  }

  function drawStar(size: number): void {
    if (!ctx) return;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI / 5) * i - Math.PI / 2;
      const r = i % 2 === 0 ? size : size * 0.45;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }

  const start = performance.now();
  const duration = 2200;
  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fade =
      elapsed > duration - 400
        ? Math.max(0, (duration - elapsed) / 400)
        : 1;
    let alive = false;
    for (const p of particles) {
      p.y += p.vy;
      p.x += p.vx;
      p.rot += p.vrot;
      p.twinkle += p.vtwinkle;
      if (p.y < canvas.height + 50 && fade > 0) {
        alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = (0.65 + Math.sin(p.twinkle) * 0.35) * fade;
        drawStar(p.size);
        ctx.restore();
      }
    }
    if (alive && elapsed < duration + 500) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}

// =========================================================================
// 4) Emojis — chuva de 🎉✨⭐🏆💯
// =========================================================================

interface EmojiParticle {
  x: number;
  y: number;
  vy: number;
  vx: number;
  size: number;
  emoji: string;
  rot: number;
  vrot: number;
  wobble: number;
  vwobble: number;
}

function playEmojiRain(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const emojis = [
    "\u{1F389}",
    "\u{1F38A}",
    "\u{2B50}",
    "\u{2728}",
    "\u{1F3C6}",
    "\u{1F973}",
    "\u{1F44F}",
    "\u{1F4AF}"
  ];
  const particles: EmojiParticle[] = [];
  const widthFactor = Math.min(1.4, Math.max(0.8, canvas.width / 900));
  const total = Math.round(35 * widthFactor);

  for (let i = 0; i < total; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: -50 - Math.random() * canvas.height * 0.6,
      vy: 8.5 + Math.random() * 6,
      vx: (Math.random() - 0.5) * 2.6,
      size: 26 + Math.random() * 20,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      rot: (Math.random() - 0.5) * 0.5,
      vrot: (Math.random() - 0.5) * 0.1,
      wobble: Math.random() * Math.PI * 2,
      vwobble: 0.09 + Math.random() * 0.07
    });
  }

  const start = performance.now();
  const duration = 2200;
  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fade =
      elapsed > duration - 400
        ? Math.max(0, (duration - elapsed) / 400)
        : 1;
    let alive = false;
    for (const p of particles) {
      p.y += p.vy;
      p.wobble += p.vwobble;
      p.x += p.vx + Math.sin(p.wobble) * 0.5;
      p.rot += p.vrot;
      if (p.y < canvas.height + 60 && fade > 0) {
        alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = fade;
        ctx.font = `${p.size}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(p.emoji, 0, 0);
        ctx.restore();
      }
    }
    if (alive && elapsed < duration + 400) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}

// =========================================================================
// 5) Balões — sobem com oscilação + barbante
// =========================================================================

interface BalloonParticle {
  x: number;
  y: number;
  vy: number;
  size: number;
  color: string;
  wobble: number;
  vwobble: number;
  wobbleAmp: number;
}

function playBalloonsRise(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const colors = [
    "#ef4444",
    "#3b82f6",
    "#22c55e",
    "#eab308",
    "#a855f7",
    "#ec4899",
    "#7665fa",
    "#f97316"
  ];
  const particles: BalloonParticle[] = [];
  const widthFactor = Math.min(1.4, Math.max(0.8, canvas.width / 900));
  const total = Math.round(16 * widthFactor);

  for (let i = 0; i < total; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: canvas.height + 30 + Math.random() * 160,
      vy: -4.5 - Math.random() * 3,
      size: 26 + Math.random() * 20,
      color: colors[Math.floor(Math.random() * colors.length)],
      wobble: Math.random() * Math.PI * 2,
      vwobble: 0.05 + Math.random() * 0.06,
      wobbleAmp: 1.2 + Math.random() * 1.8
    });
  }

  function drawBalloon(size: number, color: string): void {
    if (!ctx) return;
    // Barbante
    ctx.strokeStyle = "rgba(120, 120, 120, 0.45)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, size * 0.95);
    ctx.bezierCurveTo(
      size * 0.3,
      size * 1.6,
      -size * 0.3,
      size * 2.3,
      0,
      size * 2.9
    );
    ctx.stroke();
    // Corpo
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, size * 0.65, size * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
    // Nó
    ctx.beginPath();
    ctx.moveTo(-3, size * 0.85);
    ctx.lineTo(3, size * 0.85);
    ctx.lineTo(0, size * 0.97);
    ctx.closePath();
    ctx.fill();
    // Reflexo
    ctx.fillStyle = "rgba(255,255,255,0.40)";
    ctx.beginPath();
    ctx.ellipse(
      -size * 0.25,
      -size * 0.4,
      size * 0.13,
      size * 0.22,
      -0.4,
      0,
      Math.PI * 2
    );
    ctx.fill();
  }

  const start = performance.now();
  const duration = 2200;
  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fade =
      elapsed > duration - 400
        ? Math.max(0, (duration - elapsed) / 400)
        : 1;
    let alive = false;
    for (const p of particles) {
      p.y += p.vy;
      p.wobble += p.vwobble;
      const wx = Math.sin(p.wobble) * p.wobbleAmp;
      if (p.y > -120 && fade > 0) {
        alive = true;
        ctx.save();
        ctx.translate(p.x + wx, p.y);
        ctx.globalAlpha = fade;
        drawBalloon(p.size, p.color);
        ctx.restore();
      }
    }
    if (alive && elapsed < duration + 600) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}

// =========================================================================
// 6) Champanhe — duas taças deslizam, clink, faíscas e respingos
// =========================================================================

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  life: number;
  color: string;
}

interface InnerBubble {
  lx: number;
  ly: number;
  vy: number;
  size: number;
  life: number;
}

function playChampagneToast(): void {
  const canvas = makeAnimCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const scale = Math.min(1.5, Math.max(1.0, canvas.width / 800));
  const cx = canvas.width / 2;
  const baseY = canvas.height * 0.62;

  const meetDistHalf = 30 * scale;
  const recoilDistHalf = 78 * scale;
  const startDistHalf = canvas.width / 2 + 60 * scale;
  const tilt = 0.2;

  const sparks: Spark[] = [];
  const splashes: Spark[] = [];
  const bubblesL: InnerBubble[] = [];
  const bubblesR: InnerBubble[] = [];

  let clinkFired = false;
  const CLINK_TIME = 320;
  const slideEnd = 260;
  const recoilStart = 400;
  const recoilEnd = 900;

  function emitInnerBubble(arr: InnerBubble[]): void {
    arr.push({
      lx: (Math.random() - 0.5) * 22,
      ly: -34 + (Math.random() - 0.5) * 4,
      vy: -3 - Math.random() * 2,
      size: 1.5 + Math.random() * 2,
      life: 1
    });
  }

  function fireClink(centerX: number, centerY: number): void {
    clinkFired = true;
    for (let i = 0; i < 8; i++) emitInnerBubble(bubblesL);
    for (let i = 0; i < 8; i++) emitInnerBubble(bubblesR);
    for (let i = 0; i < 32; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 6;
      sparks.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        size: 4 + Math.random() * 5,
        life: 1,
        color: Math.random() > 0.45 ? "#fffbeb" : "#fbbf24"
      });
    }
    for (let i = 0; i < 28; i++) {
      const angle =
        -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.95;
      const speed = 3 + Math.random() * 7;
      splashes.push({
        x: centerX + (Math.random() - 0.5) * 8,
        y: centerY + (Math.random() - 0.5) * 4,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.5 + Math.random() * 3.5,
        life: 1,
        color: Math.random() > 0.55 ? "rgba(252, 211, 77, 0.92)" : "#fffbeb"
      });
    }
  }

  function easeOutBack(t: number): number {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
  function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  function drawGlass(
    x: number,
    y: number,
    angle: number,
    fillLevel: number,
    innerBubbles: InnerBubble[],
    outerAlpha: number
  ): void {
    if (!ctx) return;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.scale(scale, scale);

    if (fillLevel > 0) {
      const lvl = Math.min(1, fillLevel);
      const yTop = -88 + (1 - lvl) * 54;
      const tNorm = (yTop + 88) / 54;
      const xHalfTop = 20 - tNorm * 16;
      ctx.fillStyle = "rgba(252, 211, 77, 0.78)";
      ctx.beginPath();
      ctx.moveTo(-xHalfTop, yTop);
      ctx.lineTo(-4, -34);
      ctx.lineTo(4, -34);
      ctx.lineTo(xHalfTop, yTop);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 235, 165, 0.95)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-xHalfTop + 0.5, yTop);
      ctx.lineTo(xHalfTop - 0.5, yTop);
      ctx.stroke();
    }

    for (const b of innerBubbles) {
      if (b.life > 0) {
        ctx.fillStyle = `rgba(255, 251, 235, ${outerAlpha * b.life * 0.85})`;
        ctx.beginPath();
        ctx.arc(b.lx, b.ly, b.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.strokeStyle = "rgba(220, 230, 240, 0.9)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-20, -90);
    ctx.lineTo(-4, -34);
    ctx.moveTo(20, -90);
    ctx.lineTo(4, -34);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-20, -90);
    ctx.lineTo(20, -90);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, -34);
    ctx.lineTo(0, 4);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(0, 6, 14, 3, 0, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-15, -82);
    ctx.lineTo(-11, -50);
    ctx.stroke();

    ctx.restore();
  }

  const start = performance.now();
  const duration = 2200;
  const fillLevel = 0.72;

  function loop(t: number): void {
    if (!ctx) return;
    const elapsed = t - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fade =
      elapsed > duration - 400
        ? Math.max(0, (duration - elapsed) / 400)
        : 1;
    ctx.globalAlpha = fade;

    let leftX: number;
    let rightX: number;
    let leftAngle: number;
    let rightAngle: number;
    if (elapsed < slideEnd) {
      const k = easeOutBack(elapsed / slideEnd);
      leftX = cx - startDistHalf + (startDistHalf - meetDistHalf) * k;
      rightX = cx + startDistHalf - (startDistHalf - meetDistHalf) * k;
      const tk = Math.min(1, k);
      leftAngle = tilt * tk;
      rightAngle = -tilt * tk;
    } else if (elapsed < CLINK_TIME) {
      leftX = cx - meetDistHalf;
      rightX = cx + meetDistHalf;
      leftAngle = tilt;
      rightAngle = -tilt;
    } else if (elapsed < recoilStart) {
      const k = (elapsed - CLINK_TIME) / (recoilStart - CLINK_TIME);
      const wobble = Math.sin(k * Math.PI * 4) * 5 * (1 - k);
      leftX = cx - meetDistHalf - wobble;
      rightX = cx + meetDistHalf + wobble;
      const aw = Math.sin(k * Math.PI * 3) * 0.045 * (1 - k);
      leftAngle = tilt + aw;
      rightAngle = -tilt - aw;
    } else {
      const k = Math.min(1, (elapsed - recoilStart) / (recoilEnd - recoilStart));
      const ease = easeOutCubic(k);
      leftX = cx - meetDistHalf - (recoilDistHalf - meetDistHalf) * ease;
      rightX = cx + meetDistHalf + (recoilDistHalf - meetDistHalf) * ease;
      leftAngle = tilt * (1 - ease * 0.55);
      rightAngle = -tilt * (1 - ease * 0.55);
    }

    if (!clinkFired && elapsed >= CLINK_TIME) {
      fireClink(cx, baseY - 80 * scale);
    }

    const surfaceLocalY = -88 + (1 - fillLevel) * 54;
    function stepInner(arr: InnerBubble[]): void {
      for (const b of arr) {
        b.ly += b.vy;
        b.life -= 0.067;
        if (b.ly < surfaceLocalY) b.life = 0;
      }
    }
    stepInner(bubblesL);
    stepInner(bubblesR);

    drawGlass(leftX, baseY, leftAngle, fillLevel, bubblesL, fade);
    drawGlass(rightX, baseY, rightAngle, fillLevel, bubblesR, fade);

    for (const s of sparks) {
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.06;
      s.life -= 0.022;
      if (s.life > 0) {
        ctx.globalAlpha = fade * s.life;
        ctx.fillStyle = s.color;
        ctx.fillRect(s.x - 1, s.y - s.size / 2, 2, s.size);
        ctx.fillRect(s.x - s.size / 2, s.y - 1, s.size, 2);
      }
    }
    ctx.globalAlpha = fade;

    for (const sp of splashes) {
      sp.x += sp.vx;
      sp.y += sp.vy;
      sp.vy += 0.18;
      sp.vx *= 0.99;
      sp.life -= 0.014;
      if (sp.life > 0 && sp.y < canvas.height + 30) {
        ctx.globalAlpha = fade * sp.life * 0.9;
        ctx.fillStyle = sp.color;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, sp.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    if (elapsed < duration + 500) {
      requestAnimationFrame(loop);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(loop);
}
